import { CanMatchFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { catchError, map, of } from 'rxjs';
import { OrganizationService } from '../../services/organization/organization.service';
import { PermissionService } from '../../services/permission/permission.service';
import { OrganizationModule } from '../../enums/organization-module.enum';

/**
 * Blocks routes whose feature module the organization's plan does not include.
 *
 * Uses the cache-backed organization fetch rather than reading the signal
 * synchronously: on a cold load (refresh / deep link) the signal is still null
 * when canMatch guards run, so a synchronous read would always redirect
 * module-gated routes to '/' (the route fails to match and falls through to the
 * wildcard). The fetch is @Cacheable, so within-app navigation hits the cache
 * and the cold-load request is shared with the subscription guard.
 */
export const moduleGuardFactory = (module: OrganizationModule): CanMatchFn => {
  return () => {
    const router = inject(Router);
    const organizationService = inject(OrganizationService);
    const permissionService = inject(PermissionService);

    return organizationService.getCurrentOrganization().pipe(
      map((org) => {
        const modules = org.subscription?.modules ?? [];
        if (modules.includes(module)) return true;
        return router.createUrlTree([permissionService.getDefaultRoute()]);
      }),
      catchError(() => of(router.createUrlTree(['/']))),
    );
  };
};
